import { Injectable } from '@angular/core';
import {
    AddCampaignGQL,
    AddCampaignInput,
    Campaign,
    CampaignFilterInput,
    CampaignsGQL,
    CampaignSortInput,
    DeleteCampaignGQL,
    UpdateCampaignGQL,
    UpdateCampaignInput,
} from '../../../../graphql/generated';
import { map } from 'rxjs/operators';
import { Subject, tap } from 'rxjs';
import { GraphqlPaginatorResult } from '@knalgeel/pandora';
import { awaitLoading } from '../../shared/rxjs/await-loading';

interface GetCampaignsInput {
    customerId?: string;
    after?: string;
    first?: number;
    where?: CampaignFilterInput;
    order?: [CampaignSortInput];
}

@Injectable({
    providedIn: 'root',
})
export class CampaignService {

    private readonly _created = new Subject<string>();
    public readonly created$ = this._created.asObservable();

    private readonly _updated = new Subject<string>();
    public readonly updated$ = this._updated.asObservable();

    private readonly _deleted = new Subject<string>();
    public readonly deleted$ = this._deleted.asObservable();

    constructor(
        private readonly deleteCampaignGQL: DeleteCampaignGQL,
        private readonly updateCampaignGQL: UpdateCampaignGQL,
        private readonly addCampaignGQL: AddCampaignGQL,
        private readonly campaignsGQL: CampaignsGQL,
    ) {
    }

    public getCampaigns(input: GetCampaignsInput) {
        return this.campaignsGQL.watch(input, { fetchPolicy: 'cache-and-network' }).valueChanges.pipe(
            awaitLoading(),
            map(result => result.data.campaigns as GraphqlPaginatorResult<Campaign>),
        );
    }

    public updateCampaign(input: UpdateCampaignInput) {
        return this.updateCampaignGQL.mutate({ input }, {
            refetchQueries: [
                this.campaignsGQL.document,
            ],
        }).pipe(
            awaitLoading(),
            map(result => result.data.updateCampaign.updatedCampaignId),
            tap(campaign => this._updated.next(campaign)),
        );
    }

    public deleteCampaign(campaignId: string) {
        return this.deleteCampaignGQL.mutate({ campaignId }, {
            refetchQueries: [
                this.campaignsGQL.document,
            ],
        }).pipe(
            awaitLoading(),
            map(result => result.data.deleteCampaign.deletedCampaignId),
            tap(campaignId => this._deleted.next(campaignId)),
        );
    }

    public addCampaign(input: AddCampaignInput) {
        return this.addCampaignGQL.mutate({ input }, {
            refetchQueries: [
                this.campaignsGQL.document,
            ],
        }).pipe(
            map(result => result.data.addCampaign.addedCampaignId),
        );
    }

}
